import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Flame, Trophy, Users, Flag, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { useAuth } from "@/hooks/useAuth";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { useGeoCountry } from "@/hooks/useGeoCountry";
import { getCountryExperience } from "@/lib/i18n/countryExperience";
import { getFunnelSnapshot, subscribeFunnel } from "@/lib/conversion/funnelEngine";
import { PREVIEW_MODE } from "@/lib/previewMode";

/**
 * 🔥 Tu próxima misión — return-hook block. Decides the next action based
 * on the user's furthest funnel step. Country-agnostic via
 * countryExperience.ts (BR / MX / AR / IN).
 */
export function NextMissionCard() {
  const { isAuthenticated } = useAuth();
  const { nation } = useSelectedNation();
  const geo = useGeoCountry();
  const exp = getCountryExperience(geo.country);
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeFunnel(() => setTick((t) => t + 1)), []);

  const snap = getFunnelSnapshot();
  const has = (k: keyof typeof snap.counts) => (snap.counts[k] ?? 0) > 0;

  let title = "🔥 Tu próxima misión";
  let body = "Haz tu primera predicción del Mundial.";
  let cta = "Predecir ahora";
  let to: "/registro" | "/nation-wars" | "/quinielas" | "/" = "/quinielas";
  let Icon = Trophy;

  if (!isAuthenticated && !PREVIEW_MODE) {
    body = "Crea tu cuenta y empieza a sumar puntos para tu nación.";
    cta = "Crear cuenta";
    to = "/registro";
    Icon = Flag;
  } else if (!nation) {
    body = "Elige tu nación para activar Nation Wars.";
    cta = "Elegir nación";
    to = "/nation-wars";
    Icon = Flag;
  } else if (!has("first_prediction")) {
    body = "Tu primera predicción te da +50 puntos para tu nación.";
    cta = "Predecir ahora";
    to = "/quinielas";
    Icon = Trophy;
  } else if (!has("first_share")) {
    body = "Comparte tu predicción y multiplica los puntos de tu nación.";
    cta = "Compartir";
    to = "/";
    Icon = Flame;
  } else if (!has("first_referral")) {
    body = "Invita a un amigo: +50 puntos por cada nuevo torcedor.";
    cta = "Invitar amigo";
    to = "/";
    Icon = Users;
  } else {
    body = "Defiende la posición de tu nación con una nueva predicción.";
    cta = "Continuar";
    to = "/quinielas";
    Icon = Trophy;
  }

  useEffect(() => {
    void trackEvent("conversion_next_mission_shown", { cta, country: geo.country ?? null });
    void trackEvent("next_mission_view", { cta, country: geo.country ?? null });
  }, [cta, geo.country, tick]);

  const accent = exp?.brand.accents.primary ?? "oklch(0.65 0.22 25)";

  return (
    <section
      aria-label="Tu próxima misión"
      className="overflow-hidden rounded-2xl border p-3"
      style={{ borderColor: accent, background: "color-mix(in oklch, var(--primary) 10%, transparent)" }}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-primary">{title}</div>
          <p className="text-[12px] leading-snug text-foreground">{body}</p>
        </div>
        <Link
          to={to}
          onClick={() => {
            void trackEvent("conversion_next_mission_clicked", { cta, to });
            void trackEvent("next_mission_click", { cta, to });
          }}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-black text-primary-foreground"
        >
          {cta} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </section>
  );
}